export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_OPTIONS = [
  { key: 'mon', label: 'Mon', short: 'M' },
  { key: 'tue', label: 'Tue', short: 'T' },
  { key: 'wed', label: 'Wed', short: 'W' },
  { key: 'thu', label: 'Thu', short: 'T' },
  { key: 'fri', label: 'Fri', short: 'F' },
  { key: 'sat', label: 'Sat', short: 'S' },
  { key: 'sun', label: 'Sun', short: 'S' },
];

export const ROUND_GROUP_ORDER = ['TS', 'T1', 'T2', 'T3', 'T4', 'T5', 'TN', 'Ungrouped'];

/** Soft tints for round group headers (not exact brand colours). */
export const ROUND_GROUP_THEMES = {
  TS: { bg: '#fceaea', border: '#efb8b8', text: '#7a2e2e' },
  T1: { bg: '#eee8f8', border: '#c8b6ea', text: '#4a2f78' },
  T2: { bg: '#fff8df', border: '#ecd98a', text: '#6f5710' },
  T3: { bg: '#e7f6ec', border: '#a9d9b9', text: '#1f5a38' },
  T4: { bg: '#fceaf3', border: '#efb8d2', text: '#7a2f55' },
  T5: { bg: '#fff0e3', border: '#efc29a', text: '#7a4518' },
  TN: { bg: '#e7eefb', border: '#a8c0ef', text: '#1f3f7a' },
  Ungrouped: { bg: '#eef2f6', border: '#c2ced8', text: '#3f5568' },
};

const ROUND_SUFFIX_PATTERN = /_T(1|2|3|4|5|N|S)$/i;

export function getRoundGroupTheme(groupName) {
  return ROUND_GROUP_THEMES[groupName] || ROUND_GROUP_THEMES.Ungrouped;
}

export function emptyDays() {
  return DAY_KEYS.reduce((days, key) => {
    days[key] = 0;
    return days;
  }, {});
}

export function parseRoundGroup(firstname) {
  const normalized = (firstname || '').trim();
  const match = normalized.match(ROUND_SUFFIX_PATTERN);
  if (!match) {
    return 'Ungrouped';
  }

  return `T${match[1].toUpperCase()}`;
}

export function getCustomerEditUrl(customerId) {
  return `${process.env.VUE_APP_EPOS_API_BASE}?page=customer&mode=edit&customer_id=${customerId}`;
}

export function formatFullName(firstname, surname) {
  return `${firstname || ''} ${surname || ''}`.trim();
}

export function formatCustomerSearchLabel(customer) {
  const customerId = customer?.id ?? customer?.customerid;
  let displayName = formatFullName(customer?.firstname, customer?.surname);

  if (!displayName && customer?.text) {
    const parsed = parseNameFromAssemblyText(customer.text, customerId);
    displayName = formatFullName(parsed.firstname, parsed.surname);
  }

  const trimmedName = displayName || 'Customer';
  const accountCode = (customer?.accountcode || '').trim();

  if (accountCode) {
    return `${trimmedName} - ${customerId} - (${accountCode})`;
  }

  return `${trimmedName} - ${customerId}`;
}

export function parseNameFromAssemblyText(text, customerId) {
  const namePart = (text || '').replace(new RegExp(`\\s+${customerId}$`), '').trim();
  const lastSpace = namePart.lastIndexOf(' ');

  if (lastSpace === -1) {
    return { firstname: namePart, surname: '' };
  }

  return {
    firstname: namePart.slice(0, lastSpace),
    surname: namePart.slice(lastSpace + 1),
  };
}

export function groupAccounts(accounts) {
  const grouped = ROUND_GROUP_ORDER.reduce((groups, groupName) => {
    groups[groupName] = [];
    return groups;
  }, {});

  accounts.forEach((account) => {
    const groupName = parseRoundGroup(account.firstname);
    if (!grouped[groupName]) {
      grouped[groupName] = [];
    }
    grouped[groupName].push(account);
  });

  return ROUND_GROUP_ORDER
    .filter((groupName) => grouped[groupName].length > 0)
    .map((groupName) => ({
      name: groupName,
      accounts: grouped[groupName],
    }));
}

export function hasScheduledDays(days) {
  return DAY_KEYS.some((key) => Number(days?.[key]) === 1);
}

export function toISODateString(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatUKDate(isoDate) {
  if (!isoDate) {
    return '';
  }

  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

export function formatScheduledDaysLabel(days) {
  return DAY_OPTIONS
    .filter((option) => Number(days?.[option.key]) === 1)
    .map((option) => option.label)
    .join(', ');
}

export function formatOrderCountLabel(collectionCount, deliveryCount) {
  const parts = [];

  if (collectionCount > 0) {
    parts.push(`${collectionCount} collection${collectionCount === 1 ? '' : 's'}`);
  }

  if (deliveryCount > 0) {
    parts.push(`${deliveryCount} deliver${deliveryCount === 1 ? 'y' : 'ies'}`);
  }

  if (parts.length === 0) {
    return '0 orders';
  }

  return parts.join(', ');
}

export function formatOrderTypeLabel(type) {
  return type === 'collection' ? 'Collection' : 'Delivery';
}

export function formatOrderDateLabel(type) {
  return type === 'collection' ? 'Collection date:' : 'Due date:';
}

export function formatStatusLabel(status) {
  if (!status) {
    return '';
  }

  return String(status)
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatManifestBadgeTitle(order) {
  const manifestSetIds = String(order?.manifest_set_ids || '').trim();

  if (!manifestSetIds) {
    return 'On manifest';
  }

  return `On manifest - ${manifestSetIds}`;
}

export function normalizeOrderStatus(status) {
  return String(status || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export const COLLECTION_READY_STATUS = 'awaiting collection';

export const DELIVERY_READY_STATUSES = ['assembled', 'on van', 'delivered'];

export function isCollectionReadyStatus(status) {
  return normalizeOrderStatus(status) === COLLECTION_READY_STATUS;
}

export function isDeliveryReadyStatus(status) {
  return DELIVERY_READY_STATUSES.includes(normalizeOrderStatus(status));
}

/** Delivery readiness totals for a round group (collections excluded). */
export function getGroupDeliveryProgress(groupAccounts) {
  let ready = 0;
  let total = 0;

  (groupAccounts || []).forEach((account) => {
    (account?.deliveries || []).forEach((order) => {
      total += 1;
      if (isDeliveryReadyStatus(order.status)) {
        ready += 1;
      }
    });
  });

  return { ready, total };
}

export function isOrderStatusWarning(order) {
  if (order?.type === 'collection') {
    return !isCollectionReadyStatus(order.status);
  }

  if (order?.type === 'delivery') {
    return !isDeliveryReadyStatus(order.status);
  }

  return false;
}

/**
 * Account status indicator: cross | warning | tick | null
 */
export function getAccountStatusIndicator(account) {
  const collectionCount = Number(account?.collection_count || 0);
  const deliveryCount = Number(account?.delivery_count || 0);

  if (collectionCount < 1 || deliveryCount < 1) {
    return 'cross';
  }

  const collections = account?.collections || [];
  const deliveries = account?.deliveries || [];

  const collectionsReady = collections.length > 0
    && collections.every((order) => isCollectionReadyStatus(order.status));
  const deliveriesReady = deliveries.length > 0
    && deliveries.every((order) => isDeliveryReadyStatus(order.status));

  if (collectionsReady && deliveriesReady) {
    return 'tick';
  }

  const hasCollectionIssue = collections.some((order) => !isCollectionReadyStatus(order.status));
  const hasDeliveryIssue = deliveries.some((order) => !isDeliveryReadyStatus(order.status));

  if (hasCollectionIssue || hasDeliveryIssue) {
    return 'warning';
  }

  return 'warning';
}

export function countAccountStatusIndicators(accounts) {
  return (accounts || []).reduce((counts, account) => {
    const indicator = getAccountStatusIndicator(account);

    if (indicator === 'cross') {
      counts.cross += 1;
    } else if (indicator === 'warning') {
      counts.warning += 1;
    } else if (indicator === 'tick') {
      counts.tick += 1;
    }

    return counts;
  }, { cross: 0, warning: 0, tick: 0 });
}

export function getAccountStatusTitle(indicator) {
  switch (indicator) {
    case 'cross':
      return 'Collection or delivery order missing';
    case 'warning':
      return 'Collection or delivery status needs attention';
    case 'tick':
      return 'Collection awaiting and delivery ready';
    default:
      return '';
  }
}

export function accountNeedsWarning(account) {
  return getAccountStatusIndicator(account) === 'warning';
}
